import type {
  CallLine,
  Holder,
  CapitalReturn,
  CapitalReturnLine,
  LedgerEntry,
  RedemptionLine,
} from '../data/types';
import { daysBetween, round2 } from '../lib/format';
import { unitsAt } from './holdings';

export const DEFAULT_INTEREST_RATE = 0.1;
export const DEFAULT_NOTICE_CURE_DAYS = 14;

export function calculateCapitalReturnLines(
  classId: string,
  recordDate: string,
  returnPct: number,
  finalIncomeCentsPerUnit: number,
  unitPrice: number,
  ledger: LedgerEntry[],
  holders: Holder[] = [],
): CapitalReturnLine[] {
  const holderIds = new Set(
    ledger.filter((entry) => entry.classId === classId).map((entry) => entry.holderId),
  );
  const lines: CapitalReturnLine[] = [];
  for (const holderId of holderIds) {
    const unitsHeld = unitsAt(ledger, holderId, classId, recordDate);
    if (unitsHeld <= 0) continue;
    const unitsCancelled =
      returnPct >= 100 ? unitsHeld : Math.floor(unitsHeld * (returnPct / 100) * 10_000) / 10_000;
    const holder = holders.find((item) => item.id === holderId);
    const grossIncome = round2((unitsHeld * finalIncomeCentsPerUnit) / 100);
    const incomeWithheld = !holder
      ? 0
      : holder.residency !== 'AU'
        ? round2(grossIncome * 0.1)
        : holder.tfnStatus === 'NOT_QUOTED'
          ? round2(grossIncome * 0.47)
          : 0;
    lines.push({
      holderId,
      unitsHeld,
      unitsCancelled,
      amount: round2(unitsCancelled * unitPrice),
      finalIncome: round2(grossIncome - incomeWithheld),
      incomeWithheld,
    });
  }
  return lines.sort((left, right) => right.amount - left.amount);
}

export function capitalReturnEntries(
  capitalReturn: CapitalReturn,
  unitPrice: number,
  actor: string,
): Omit<LedgerEntry, 'seq' | 'id' | 'postedAt'>[] {
  const entries: Omit<LedgerEntry, 'seq' | 'id' | 'postedAt'>[] = [];
  for (const line of capitalReturn.lines) {
    const base = {
      fundId: capitalReturn.fundId,
      classId: capitalReturn.classId,
      holderId: line.holderId,
      effectiveDate: capitalReturn.paymentDate,
      eventId: capitalReturn.id,
      postedBy: actor,
      approvedBy: capitalReturn.approvedBy,
    };
    if (line.finalIncome > 0) {
      entries.push({
        ...base,
        type: 'DISTRIBUTION',
        units: 0,
        amount: line.finalIncome,
        reference: capitalReturn.kind === 'FINAL' ? 'FINAL DIST' : 'INCOME',
        memo: 'Final income to repayment date',
      });
    }
    if (line.incomeWithheld > 0) {
      entries.push({
        ...base,
        type: 'WITHHOLDING',
        units: 0,
        amount: line.incomeWithheld,
        reference: 'WHT FINAL',
        memo: 'Tax withheld from final income',
      });
    }
    entries.push({
      ...base,
      type: 'CAPITAL_RETURN',
      units: -line.unitsCancelled,
      amount: line.amount,
      unitPrice,
      reference: capitalReturn.kind === 'FINAL' ? 'WIND-UP' : 'CAP RETURN',
      memo:
        capitalReturn.kind === 'FINAL'
          ? 'Capital returned in full; units cancelled'
          : `${capitalReturn.returnPct}% of capital returned`,
    });
  }
  return entries;
}

export function priceRedemptionLines(
  lines: RedemptionLine[],
  unitPrice: number,
  classUnits: number,
  gatePct: number,
): { lines: RedemptionLine[]; gated: boolean; scale: number } {
  const requested = lines.reduce((total, line) => total + line.unitsRequested, 0);
  const cap = classUnits * (gatePct / 100);
  const gated = gatePct > 0 && requested > cap;
  const scale = gated ? cap / requested : 1;
  return {
    gated,
    scale,
    lines: lines.map((line) => {
      const unitsRedeemed = Math.floor(line.unitsRequested * scale * 10_000) / 10_000;
      return { ...line, unitsRedeemed, amount: round2(unitsRedeemed * unitPrice) };
    }),
  };
}

export function daysOverdue(dueDate: string, today: string): number {
  return Math.max(0, daysBetween(dueDate, today));
}

export function defaultInterest(line: CallLine, dueDate: string, today: string): number {
  if (line.status === 'PAID' || line.interestWaived) return 0;
  const days = daysOverdue(dueDate, today);
  return round2(((line.amount - line.paid) * DEFAULT_INTEREST_RATE * days) / 365);
}
